import { buildFeedUiTabs, FEED_CATEGORY_DEFINITIONS, type FeedContentCategorySlug } from '@upme/contracts';
import { importQueueRu } from '../../../i18n/ru/import-queue';
import type { ImportQueueT } from '../lib/format';

export type ImportQueuePublishKind = 'event' | 'news';

export interface ImportQueueFeedPublishAction {
  slug: FeedContentCategorySlug;
  label: string;
  kind: ImportQueuePublishKind;
  buttonClassName: string;
}

const BUTTON_CLASS_BY_KIND: Record<ImportQueuePublishKind, string> = {
  event:
    'border-violet-500/60 text-violet-800 hover:bg-violet-50 dark:text-violet-300 dark:hover:bg-violet-950/40',
  news: 'border-sky-500/50 text-sky-900 hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-950/40',
};

/** Порядок как в mini-app L2 (без служебного all). */
function feedTabContentSlugs(): FeedContentCategorySlug[] {
  return buildFeedUiTabs()
    .map((t) => t.slug)
    .filter((s): s is FeedContentCategorySlug => s !== 'all');
}

function getByPath(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (acc, part) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[part] : undefined),
      obj,
    );
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => String(vars[key] ?? ''));
}

const defaultT: ImportQueueT = (key, vars) => {
  const raw = getByPath(importQueueRu, key);
  return typeof raw === 'string' ? interpolate(raw, vars) : key;
};

/**
 * `t` — прокидывается вызывающим компонентом из `useTranslation('import-queue')`
 * (реальная локаль пользователя). Без явного `t` (юнит-тесты) — фолбэк на RU.
 * `def.label` из `@upme/contracts` — русский домен-текст, вне scope этой задачи
 * (только admin-ui/features/import-queue) — остаётся нелокализованным.
 */
export function buildImportQueueFeedPublishActions(
  t: ImportQueueT = defaultT,
): ImportQueueFeedPublishAction[] {
  return feedTabContentSlugs().map((slug) => {
    const def = FEED_CATEGORY_DEFINITIONS[slug];
    const kind: ImportQueuePublishKind = slug === 'events' ? 'event' : 'news';
    return {
      slug,
      label: t('feedPublishActions.labelPrefix', { label: def.label }),
      kind,
      buttonClassName: BUTTON_CLASS_BY_KIND[kind],
    };
  });
}
